import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Urls } from '../../context';
import { MilestoneReport, MilestoneStatusResponses } from '../../models';
import { DateUtils } from '../../utils';
import { FormatCurrency } from '../../utils/number';
import { CerebroDrawer } from '../CerebroDrawer';
import { CerebroHeading } from '../CerebroHeading';
import { CerebroLink } from '../CerebroLink';
import { CerebroText } from '../CerebroText';

interface LenderMilestoneDetailsProps {
	report: MilestoneReport | null | undefined;
	close: () => void;
}

export const LenderMilestoneDetails: React.FC<LenderMilestoneDetailsProps> = ({ report, close }) => {
	const navigate = useNavigate();
	const { guid } = useParams<'guid'>();

	const onNavitateToCalls = () => {
		navigate(`/${guid}/${Urls.Calls.Root}`);
		close();
	};

	const onNavigateToTermSheets = () => {
		navigate(`/${guid}/${Urls.TermSheets.Root}`);
		close();
	};

	const getLenderTermSheetText = (report: MilestoneReport | null | undefined): JSX.Element => {
		if (report?.readyForCall == 'Yes' && report?.termSheetSubmitted != 'Yes') {
			return (
				<CerebroText>
					<CerebroLink onClick={() => onNavigateToTermSheets()}>Click here</CerebroLink> to upload your term
					sheet.
				</CerebroText>
			);
		}

		if (report?.readyForCall == 'Yes' && report?.termSheetSubmitted == 'Yes') {
			const submitDate =
				report?.termSheetSubmittedOn != null
					? DateUtils.FormatStringAsShortDate(report?.termSheetSubmittedOn.toString())
					: '';

			return (
				<CerebroText>
					Submitted on {submitDate}; have a revision to submit?{' '}
					<CerebroLink onClick={() => onNavigateToTermSheets()}>Click here</CerebroLink> to upload.
				</CerebroText>
			);
		}

		return <span>&nbsp;</span>;
	};

	const getLenderCallBorrowerText = (report: MilestoneReport | null | undefined): JSX.Element => {
		if (report == null) return <span>&nbsp;</span>;

		if (report?.readyForCall == 'Yes') {
			if (report?.callStatus == 'Proposed') {
				return <CerebroText>Call proposed, pending scheduling</CerebroText>;
			}

			if (report?.callStatus == 'Scheduled') {
				return <CerebroText>Call scheduled</CerebroText>;
			}

			if (report?.dataRoomCallGuid != null && report?.callDate != null && report?.callStatus == 'Completed') {
				return (
					<CerebroText>
						Call Completed on {DateUtils.FormatStringAsShortDate(report?.callDate.toString())}; Need another{' '}
						call? <CerebroLink onClick={() => onNavitateToCalls}>Request one here</CerebroLink>
					</CerebroText>
				);
			}

			return <span>&nbsp;</span>;
		}

		if (report?.documentsDownloaded == 'Yes' && report?.callDate != null) {
			return (
				<CerebroText>
					When ready, <CerebroLink onClick={() => onNavitateToCalls}>click here</CerebroLink> to request a
					call with the borrower.
				</CerebroText>
			);
		}

		if (report?.readyForCall != 'OptOut') {
			return <span>&nbsp;</span>;
		}

		return <CerebroText>Call requested, pending scheduling.</CerebroText>;
	};

	const getLenderFundedText = (report: MilestoneReport): JSX.Element => {
		const fundAmount = report.amountFunded != null ? FormatCurrency(report.amountFunded) : '';
		const fundDate =
			report.dateFunded != null ? DateUtils.FormatStringAsShortDate(report.dateFunded.toString()) : '';

		return (
			<CerebroText>
				{fundAmount} funded on {fundDate}
			</CerebroText>
		);
	};

	return (
		<CerebroDrawer open={report != null} onClose={close}>
			<CerebroHeading size="h1" gutterBottom>
				Lender Progress Report Details
			</CerebroHeading>
			<CerebroHeading size="h3" gutterBottom>
				{report?.lenderName} ({report?.bankName})
			</CerebroHeading>

			<TableContainer sx={{ marginTop: 3 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Stage</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Details</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!report?.activeUser && (
							<TableRow>
								<TableCell colSpan={3} sx={{ textAlign: 'center' }}>
									Inactive Lender
								</TableCell>
							</TableRow>
						)}
						{!report?.activeUser && report?.hasRoomOptOutReasons && (
							<TableRow>
								<TableCell>Opt-Out Reasons</TableCell>
								<TableCell colSpan={2}>
									{report.roomDeclineReasonList.map(reason => (
										<CerebroText key={'decline-' + reason}>{reason}</CerebroText>
									))}
									{report.otherRoomDeclineReason != '' && (
										<CerebroText>{report.otherRoomDeclineReason}</CerebroText>
									)}
									{report.roomReasonDetail != '' && (
										<CerebroText>{report.roomReasonDetail}</CerebroText>
									)}
								</TableCell>
							</TableRow>
						)}
						<TableRow>
							<TableCell>Teaser</TableCell>
							<TableCell>{getResponseText(report?.teaser)}</TableCell>
							<TableCell>
								{report?.teaserDate != null && (
									<CerebroText>
										{DateUtils.FormatStringAsShortDate(report?.teaserDate.toString())}
									</CerebroText>
								)}
								{report?.teaser == 'OptOut' && (
									<>
										{report.teaserDeclineReasonList.map(reason => (
											<CerebroText key={'teaser-' + reason}>{reason}</CerebroText>
										))}
										{report.otherDeclineReason != '' && (
											<CerebroText>{report.otherDeclineReason}</CerebroText>
										)}
									</>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Deal Accessed</TableCell>
							<TableCell>{getResponseText(report?.accessDeal)}</TableCell>
							<TableCell>
								{report?.lastAccessedOn != null && (
									<CerebroText>
										{DateUtils.FormatStringAsShortDate(report?.lastAccessedOn.toString())}
									</CerebroText>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Documents Reviewed</TableCell>
							<TableCell>{getResponseText(report?.documentsDownloaded)}</TableCell>
							<TableCell>
								{report?.documentsDownloadedDate != null && (
									<CerebroText>
										{DateUtils.FormatStringAsShortDate(report?.documentsDownloadedDate.toString())}
									</CerebroText>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Call with Borrower</TableCell>
							<TableCell>{getCallResponseText(report)}</TableCell>
							<TableCell>{getLenderCallBorrowerText(report)}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Term Sheet Submitted</TableCell>
							<TableCell>{getResponseText(report?.termSheetSubmitted)}</TableCell>
							<TableCell>{getLenderTermSheetText(report)}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Underwriting</TableCell>
							<TableCell>{getResponseText(report?.underwriting)}</TableCell>
							<TableCell>
								{report?.underwriting == 'Yes' && (
									<CerebroText>
										{report.funded == 'Yes'
											? 'Underwriting diligence in progress'
											: 'Term Sheet being reviewed'}
									</CerebroText>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Funded</TableCell>
							<TableCell>{getResponseText(report?.funded)}</TableCell>
							<TableCell>{report?.funded == 'Yes' && getLenderFundedText(report)}</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</CerebroDrawer>
	);
};

function getResponseText(response?: MilestoneStatusResponses) {
	if (response == 'Yes') {
		return 'Complete';
	}
	if (response == 'OptOut') {
		return 'Opt-Out';
	}

	return 'Pending';
}

function getCallResponseText(report: MilestoneReport | null | undefined) {
	if (report == null) return 'Pending';
	if (report.callStatus == 'Proposed' || report.callStatus == 'Scheduled') {
		return 'Pending';
	}

	const response = report.readyForCall;
	if (report.callStatus == 'Completed') {
		return 'Complete';
	}
	if (response == 'OptOut') {
		return 'Opt-Out';
	}

	return 'Pending';
}
